import store from 'redux/store';
import { isNumber, isString } from 'lodash-es';
import { formatNumberByLocale } from 'utilities';
import {
  MANDATORY_CARD_FIELDS,
  REGEXES,
  ERROR_MESSAGES,
  FORCE_STRICT_REGEXES,
  FIELD_NAMES as TOKENIZATION_FIELD_NAMES,
  CARD_ERRORS,
  FIELD_NAMES
} from 'consts';
import { isCardNumberValid } from './card';

const EMPTY_FIELD_ERROR = ERROR_MESSAGES.required_field;
const INVALID_FIELD_ERROR = ERROR_MESSAGES.invalid_field;

function isValidIBANNumber(input) {
  const CODE_LENGTHS = {
    AD: 24,
    AE: 23,
    AT: 20,
    AZ: 28,
    BA: 20,
    BE: 16,
    BG: 22,
    BH: 22,
    BR: 29,
    CH: 21,
    CY: 28,
    CZ: 24,
    DE: 22,
    DK: 18,
    DO: 28,
    EE: 20,
    ES: 24,
    FI: 18,
    FO: 18,
    FR: 27,
    GB: 22,
    GI: 23,
    GL: 18,
    GR: 27,
    GT: 28,
    HR: 21,
    HU: 28,
    IE: 22,
    IL: 23,
    IS: 26,
    IT: 27,
    JO: 30,
    KW: 30,
    KZ: 20,
    LB: 28,
    LI: 21,
    LT: 20,
    LU: 20,
    LV: 21,
    MC: 27,
    MD: 24,
    ME: 22,
    MK: 19,
    MR: 27,
    MT: 31,
    MU: 30,
    NL: 18,
    NO: 15,
    PK: 24,
    PL: 28,
    PS: 29,
    PT: 25,
    QA: 29,
    RO: 24,
    RS: 22,
    SA: 24,
    SE: 24,
    SI: 19,
    SK: 24,
    SM: 27,
    TN: 24,
    TR: 26,
    AL: 28,
    BY: 28,
    CR: 22,
    EG: 29,
    GE: 22,
    IQ: 23,
    LC: 32,
    SC: 31,
    ST: 25,
    SV: 28,
    TL: 23,
    UA: 29,
    VA: 22,
    VG: 24,
    XK: 20,
    BI: 27,
    DJ: 27,
    FK: 18,
    LY: 25,
    MN: 20,
    NI: 28,
    RU: 33,
    SO: 23,
    SD: 18
  };
  let iban = String(input)
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, ''), // keep only alphanumeric characters
    code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/), // match and capture (1) the country code, (2) the check digits, and (3) the rest
    digits;
  // check syntax and length
  if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
    return false;
  }
  // rearrange country code and check digits, and convert chars to ints
  digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, function (letter) {
    return letter.charCodeAt(0) - 55;
  });
  // final check
  return mod97(digits) === 1;
}

function mod97(string) {
  let checksum = string.slice(0, 2),
    fragment;
  for (let offset = 2; offset < string.length; offset += 7) {
    fragment = String(checksum) + string.substring(offset, offset + 7);
    checksum = parseInt(fragment, 10) % 97;
  }
  return checksum;
}

export const isValidIBAN = (iban, errors, regex) => {
  const { should_not_validate_iban: shouldNotValidateIban } =
    store?.getState()?.global?.configData || {};

  if (shouldNotValidateIban) return;

  const errorMessage = errors?.[FIELD_NAMES.iban] || INVALID_FIELD_ERROR;

  if (regex) {
    const isValid = testRegex({ regex, value: iban, name: FIELD_NAMES.iban });
    return isValid ? undefined : errorMessage;
  }

  if (REGEXES.NOT_IBAN_REGEX.test(iban)) {
    return errorMessage;
  }
  return isValidIBANNumber(iban) ? undefined : errorMessage;
};

// \p{L} matches all language characters of any language.
export const isAlphaNumericOrSpace = value => /^(\p{L}|\s|\d)*$/gu.test(value);

// TODO - redundant when finish redesign
export const futureMMYYValidation = date => {
  if (!date) return 'required_field_error';
  const trimmedDate = date.replace(/\s/g, '');
  if (!trimmedDate) return 'required_field_error';
  const [MM, YY] = trimmedDate.split('/');
  if (!MM || !YY || MM.length !== 2 || YY.length !== 2)
    return 'card_expiration_incomplete';

  const expiration = new Date(`20${YY}`, MM - 1);
  const today = new Date();

  if (MM < 1 || MM > 12) return 'card_expiration_month_not_valid';

  const yearsToExpiration = expiration.getFullYear() - today.getFullYear();
  if (yearsToExpiration > 19) {
    return 'card_expiration_year_not_valid';
  }

  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  if (Date.parse(expiration) < Date.parse(firstDayOfMonth)) {
    return 'invalid_past_card_expiration';
  }

  if (!REGEXES.expiration.test(trimmedDate)) {
    return 'invalid_card_expiration';
  }
};

export const cardNumberValidation = (value = '', errors = {}, regex = '') => {
  const isValid = isCardNumberValid(value, regex);

  if (!isValid) {
    return (
      errors[MANDATORY_CARD_FIELDS.number] ||
      ERROR_MESSAGES[TOKENIZATION_FIELD_NAMES.card_number] ||
      undefined
    );
  }
};
//

export const isEmptyString = value => {
  const castedString = String(value);
  const isEmptyString = !castedString || !castedString.length;
  return !!isEmptyString;
};

export const testRegex = ({ regex, value, name }) => {
  let strictRegex = regex;

  if (FORCE_STRICT_REGEXES[name]) {
    if (strictRegex[0] !== '^') {
      strictRegex = `^${strictRegex}`;
    }

    if (strictRegex[strictRegex.length - 1] !== '$') {
      strictRegex = `${strictRegex}$`;
    }
  }

  let isValid = true;
  try {
    const regexExpression = new RegExp(strictRegex, 'g');
    isValid = regexExpression.test(value);
  } catch (err) {}

  return !!isValid;
};

export const validateDynamicRegex = ({ regex, value, name }) => {
  let message = '';

  try {
    const isValid = testRegex({ regex, value, name });
    if (!isValid) {
      message = INVALID_FIELD_ERROR;
    }
  } catch (err) {
    message = isEmptyString(value) ? EMPTY_FIELD_ERROR : '';
  }

  return message;
};

const SPECIFIC_VALIDATIONS = {
  [TOKENIZATION_FIELD_NAMES.iban]: value => isValidIBAN(value),
  [MANDATORY_CARD_FIELDS.number]: (value, errors, regex) =>
    cardNumberValidation(value, errors, regex),
  [MANDATORY_CARD_FIELDS.name]: (value, errors) => {
    const isValid = isAlphaNumericOrSpace(value);
    if (!isValid) {
      return errors[MANDATORY_CARD_FIELDS.name] || '';
    }
    return '';
  },
  [MANDATORY_CARD_FIELDS.expiration]: value => futureMMYYValidation(value),
  [TOKENIZATION_FIELD_NAMES.card_expiration]: value => futureMMYYValidation(value),
  [TOKENIZATION_FIELD_NAMES.card_number]: (value, errors, regex) =>
    cardNumberValidation(value, errors, regex)
};

export const validateInputCheck = ({ regex, value, name, errors = {} }) => {
  const isEmptyValue = isEmptyString(value);
  if (isEmptyValue) {
    return EMPTY_FIELD_ERROR;
  }

  const fieldValidation = SPECIFIC_VALIDATIONS[name];
  if (fieldValidation) {
    return fieldValidation(value, errors, regex);
  }

  if (regex) {
    return validateDynamicRegex({ regex, value, name });
  }

  return '';
};

const getLimitFromQualifierRegex = regex => {
  const regexAsString = String(regex);
  const limitArray = regexAsString.match(
    REGEXES.LIMIT_DIGITS_AS_NUMERIC_QUALIFIER_REGEX_EXTRACTOR
  );
  // element at index 0 includes the non capturing group, thus we are concerned only with the element at index 1 which is the capturing group
  const firstMatch = limitArray?.[1];
  const limitAsNumber = firstMatch && Number(firstMatch);
  if (!Number.isFinite(limitAsNumber)) {
    return;
  }
  return limitAsNumber;
};

const getLimitFromBracketsRegex = regex => {
  const regexAsString = String(regex);
  const limitArray = regexAsString.match(
    REGEXES.LIMIT_DIGITS_AS_BRACKETS_REGEX_EXTRACTOR
  );
  const length = limitArray?.length;
  if (!length) {
    return;
  }
  return length;
};

export const extractDigitsWithLimitRegex = regex => {
  if (!regex) {
    return;
  }
  if (REGEXES.LIMIT_DIGITS_AS_BRACKETS_REGEX.some(r => r.test(regex))) {
    return getLimitFromBracketsRegex(regex);
  }
  if (REGEXES.LIMIT_DIGITS_AS_NUMERIC_QUALIFIER_REGEX.some(r => r.test(regex))) {
    return getLimitFromQualifierRegex(regex);
  }
  if (REGEXES.LIMIT_DIGITS_AS_MONTH_STRING.includes(regex)) {
    return 2;
  }
  if (REGEXES.LIMIT_DIGITS_AS_YEAR_STRING.includes(regex)) {
    return 4;
  }
  if (REGEXES.LIMIT_DIGITS_AS_NINE_RANGE_STRING.includes(regex)) {
    return 9;
  }
};

export const isTransferCurrencyValid = (value, selectedPMTCurrencies) => {
  if (!isString(value) || value === '') {
    return false;
  }
  if (!Array.isArray(selectedPMTCurrencies) || !selectedPMTCurrencies.length) {
    return false;
  }
  return selectedPMTCurrencies.some(
    c => c.toLocaleLowerCase() === value.toLocaleLowerCase()
  );
};

export const validateAndGetTransferAmountError = ({
  amount,
  selectedCardCurrencyMinMaxAmountMappings,
  currency,
  t
}) => {
  if (!isString(currency) || currency === '') {
    return 'card_to_card.transfer.invalid_transfer_amount_no_currency_dependency';
  }
  if (!isNumber(amount)) {
    return 'card_to_card.transfer.invalid_transfer_amount_empty';
  }
  if (amount === 0) {
    return 'card_to_card.transfer.invalid_transfer_amount_greater_than_zero';
  }
  if (
    !Array.isArray(selectedCardCurrencyMinMaxAmountMappings) ||
    !selectedCardCurrencyMinMaxAmountMappings.length
  ) {
    return 'card_to_card.transfer.invalid_currency_min_max_amount_mapping';
  }
  const currentCurrencyMappingObject = selectedCardCurrencyMinMaxAmountMappings.find(
    mapping => mapping.currency === currency
  );
  if (!currentCurrencyMappingObject) {
    return 'card_to_card.transfer.invalid_currency_mapping_not_found';
  }
  const {
    min_amount: minAmount,
    max_amount: maxAmount,
    decimal_scale: decimalScale
  } = currentCurrencyMappingObject;
  if (!REGEXES.TRANSFER_FUNDS_AMOUNT.test(amount.toString())) {
    return `${t(
      'card_to_card.transfer.invalid_transfer_amount_first_part'
    )} ${decimalScale} ${t('card_to_card.transfer.invalid_transfer_amount_second_part')}`;
  }
  if (isNumber(minAmount) && amount < minAmount) {
    return `${t(
      'card_to_card.transfer.invalid_transfer_min_amount'
    )} ${formatNumberByLocale(minAmount, currency)}`;
  }
  if (isNumber(maxAmount) && amount > maxAmount) {
    return `${t(
      'card_to_card.transfer.invalid_transfer_max_amount'
    )} ${formatNumberByLocale(maxAmount, currency)}`;
  }
  return '';
};

export const isValidCVV = value => {
  const cvvRegex = REGEXES.cvv;
  return cvvRegex.test(value) ? undefined : CARD_ERRORS.cvv;
};

export const isValidMonth = monthValue => {
  const trimmedMonth = monthValue?.trim();
  return !(trimmedMonth.length < 2 || trimmedMonth < 1 || trimmedMonth > 12);
};

const separateFutureMMYYValidation = date => {
  if (!date) return 'required_field_error';
  const trimmedDate = date.replace(/\s/g, '');
  if (!trimmedDate) return 'required_field_error';
  const [MM, YY] = trimmedDate.split('/');

  const expiration = new Date(`20${YY}`, MM - 1);
  const today = new Date();

  const yearsToExpiration = expiration.getFullYear() - today.getFullYear();
  if (yearsToExpiration > 19) {
    return 'card_expiration_year_not_valid';
  }

  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  if (
    YY.length < 2 ||
    (MM.length === 2 && Date.parse(expiration) < Date.parse(firstDayOfMonth))
  ) {
    return 'invalid_past_card_expiration';
  }

  return '';
};

export const isValidFutureYear = (yearValue, monthValue) => {
  return !separateFutureMMYYValidation(`${monthValue}/${yearValue}`);
};

export const dateOfBirthValidation = (value = '', errors = {}, regex = '') => {
  const isValid = testRegex({ regex, value });

  if (!isValid) return 'invalid_field';

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [day, month, year] = value?.split('/')?.map(Number) || [];
    const inputDate = new Date(year, month - 1, day);
    const today = new Date();

    // Check if the date components are valid and not in the future
    if (
      year > today.getFullYear() ||
      (year === today.getFullYear() && month - 1 > today.getMonth()) ||
      (year === today.getFullYear() &&
        month - 1 === today.getMonth() &&
        day > today.getDate())
    ) {
      return 'invalid_future_date';
    } else if (
      day !== inputDate.getDate() ||
      month - 1 !== inputDate.getMonth() ||
      year !== inputDate.getFullYear()
    ) {
      return 'invalid_date';
    }
  }
};
